import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@loot-os/ui/components/navigation-menu";
import { cn } from "@loot-os/ui/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import { Gamepad2Icon, MenuIcon, SearchIcon } from "lucide-react";

import type { NavLink } from "../lib/nav.js";
import { useBalance, useShopper } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import { BalanceBadge } from "./BalanceBadge.js";
import { NotificationBell } from "./notifications/NotificationBell.js";

/** The design's nav items: outline buttons, the Games trigger among them. */
const NAV_BUTTON = buttonVariants({ variant: "outline" });

/**
 * The design's top navigation: the brand, the Games menu and the other pages from `md`, then
 * signing in (or the balance and the notification bell), search and the menu. Below `md` the
 * links live in the menu and the tab bar.
 */
export function SiteHeader({
  links,
  onOpenMenu,
  onSearch,
}: {
  links: NavLink[];
  onOpenMenu: () => void;
  onSearch: () => void;
}) {
  const { user } = useShopper();
  const balance = useBalance().data ?? null;
  const { config } = useTenant();
  const pathname = useLocation({ select: (location) => location.pathname });
  const games = links.filter((link) => link.group === "games");
  const pages = links.filter((link) => link.group === "pages");
  const isActive = (link: NavLink) =>
    link.exact ? pathname === link.to : pathname === link.to || pathname.startsWith(`${link.to}/`);

  return (
    <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-14 w-full max-w-7xl items-center gap-3 px-4 md:gap-5">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 text-base font-semibold tracking-tight"
        >
          {config.branding.logoUrl ? (
            <img src={config.branding.logoUrl} alt="" className="h-7 w-auto" />
          ) : null}
          <span className="truncate">{config.name}</span>
        </Link>
        {/* Fail-open: only an explicit "test" shows the pill, so a stale cached
            config without the field renders nothing rather than mislabeling. */}
        {config.mode === "test" && (
          <span className="shrink-0 rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
            Test mode
          </span>
        )}
        <NavigationMenu aria-label="Main" className="hidden md:flex">
          <NavigationMenuList className="gap-2">
            {games.length > 0 && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={NAV_BUTTON}>
                  <Gamepad2Icon />
                  Games
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-48 gap-1">
                    {games.map((link) => (
                      <li key={link.to}>
                        <NavigationMenuLink
                          render={<Link to={link.to} />}
                          active={isActive(link)}
                          className="gap-2"
                        >
                          <link.icon />
                          {link.label}
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            )}
            {pages.map((link) => (
              <NavigationMenuItem key={link.to} className="hidden lg:block">
                <NavigationMenuLink
                  render={<Link to={link.to} />}
                  active={isActive(link)}
                  className={cn(NAV_BUTTON, "p-2.5 data-active:text-foreground")}
                >
                  <link.icon />
                  {link.label}
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {user ? (
            <>
              <BalanceBadge balance={balance} />
              <span className="hidden text-sm text-muted-foreground xl:inline">{user.email}</span>
              <NotificationBell />
            </>
          ) : (
            <>
              <Link to="/login" className={buttonVariants({ variant: "outline" })}>
                Log in
              </Link>
              <Link to="/login" className={buttonVariants()}>
                Sign up
              </Link>
            </>
          )}
          <Button variant="secondary" size="icon" aria-label="Search" onClick={onSearch}>
            <SearchIcon />
          </Button>
          <Button variant="secondary" size="icon" aria-label="Menu" onClick={onOpenMenu}>
            <MenuIcon />
          </Button>
        </div>
      </div>
    </header>
  );
}
